import { useRouter } from 'next/navigation';
import { MessageDescriptor } from 'react-intl';

import WebAppUpsellFlow from '@/components/SimplifiedSignup/configs/WebAppUpsell/Flow';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useUserState } from '@/hooks/store';
import useQueryParams from '@/hooks/utils/useQueryParams';

import { useManageSubscription } from '../useManageSubscription';
import messages from './messages';
import { useUpsellUserState } from './useUpsellUserState';

export const useUpsellBannerContent = () => {
	const router = useRouter();
	const userUpsellState = useUpsellUserState();
	const user = useUserState();
	const { isModalOpen, setIsModalOpen } = useSimplifiedSignupModalContext();
	const query = useQueryParams();
	const { hasFamilyPlan: isFamilyPlanUser } = useManageSubscription();

	const isSubscriptionIneligible = ['b2b_dependent', 'b2b', 'ios', 'android', 'truemed'].includes(
		user?.subscription?.type ?? '',
	);
	const isEligibleForFamilyPlanUpgrade = !isFamilyPlanUser && !isSubscriptionIneligible;
	const isLifetimeUser = user?.subscription?.is_lifetime;

	const isFamilyPlanUpsellBannerEligible =
		userUpsellState === 'activeCalmPremiumUser' && isEligibleForFamilyPlanUpgrade && !isLifetimeUser;

	const onBannerClick = async () => {
		if (isFamilyPlanUpsellBannerEligible) {
			await router.push('/family');
			return;
		}
		setIsModalOpen(true);
	};

	const getUpsellBannerState = (): MessageDescriptor | null => {
		/* logic to determine what the copy should be */

		const isOrganicTraffic = query?.utm_medium !== 'paid';

		if (!isOrganicTraffic) {
			return null;
		}

		switch (userUpsellState) {
			case 'loggedOut':
				return messages.userLoggedOut;
			case 'neverTrialed':
				return messages.neverSubsAndChurnedUsersWithoutTrials;
			case 'churnedAndTrialed':
				return messages.churnedUsersWithTrial;
			case 'activeCalmPremiumUser':
				return isFamilyPlanUpsellBannerEligible ? messages.loggedInUser : null;
			// No upsell banner while the subscription is paused
			case 'paused':
				return null;
			default:
				return null;
		}
	};

	return {
		onClick: onBannerClick,
		config: WebAppUpsellFlow,
		isModalOpen,
		upsellCopy: getUpsellBannerState(),
		isFamilyPlanUpsellBannerEligible,
	};
};
